import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { 
  Link2, Code, QrCode, Copy, Download, Check, 
  ExternalLink, AlertCircle
} from 'lucide-react';

const BASE_URL = window.location.origin;

export default function SharePage() {
  const [sourceName, setSourceName] = useState('');
  const [buttonText, setButtonText] = useState('Open My Direct Debits');
  const [copiedField, setCopiedField] = useState(null);
  const qrRef = useRef(null);

  const getShareUrl = () => {
    if (sourceName.trim()) {
      return `${BASE_URL}?source=${encodeURIComponent(sourceName.trim())}`;
    }
    return BASE_URL;
  };

  const getButtonCode = () => {
    const url = getShareUrl();
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 20px;background:#27B340;color:#fff;text-decoration:none;border-radius:8px;font-weight:600;">
  ${buttonText || 'Open My Direct Debits'}
</a>`;
  };

  const getIframeCode = () => {
    const url = getShareUrl();
    return `<iframe
  src="${url}"
  width="100%"
  height="800"
  style="border:0;border-radius:12px;"
  allow="payment"
></iframe>`;
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadQRCode = async () => {
    if (!qrRef.current) return;
    
    try {
      const canvas = await html2canvas(qrRef.current, {
        backgroundColor: '#ffffff',
        scale: 3
      });
      
      const link = document.createElement('a');
      link.download = `mdd-qr-${sourceName || 'direct'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR code downloaded!');
    } catch (err) {
      toast.error('Failed to download QR code');
    }
  };

  const CopyButton = ({ text, field }) => (
    <Button
      onClick={() => copyToClipboard(text, field)}
      variant="outline"
      size="sm"
      className="gap-2"
    >
      {copiedField === field ? (
        <>
          <Check className="w-4 h-4 text-primary" />
          Copied
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          Copy
        </>
      )}
    </Button>
  );

  return (
    <PageLayout showNav>
      <div className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        <div>
          <Link to="/settings" className="text-primary text-sm hover:underline">
            ← Back to Settings
          </Link>
          <h1 className="font-heading font-bold text-dark text-2xl mt-2">
            Want to Recommend, Embed or Share Our App?
          </h1>
          <p className="text-text-secondary mt-1">
            Generate shareable links, embed codes, and QR codes for distribution
          </p>
        </div>

        {/* Source Configuration */}
        <div className="card-default p-6 space-y-4">
          <h2 className="font-heading font-semibold text-dark text-lg flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            Configure Source Tracking
          </h2>
          <p className="text-sm text-text-secondary">
            Add a source name to track where your signups come from
          </p>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Source Name
              </label>
              <Input
                type="text"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                placeholder="e.g. bank_a, partner_xyz"
                className="input-field"
                data-testid="source-name-input"
              />
              <p className="text-xs text-text-light mt-1">
                Letters, numbers, underscores only
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Button Text (optional)
              </label>
              <Input
                type="text"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                placeholder="Open My Direct Debits"
                className="input-field"
                data-testid="button-text-input"
              />
            </div>
          </div>
        </div>

        {/* Direct Link */}
        <div className="card-default p-6 space-y-4">
          <h2 className="font-heading font-semibold text-dark text-lg flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-primary" />
            Direct Link
          </h2>
          
          <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-between gap-4">
            <code className="text-sm text-text-primary break-all flex-1">
              {getShareUrl()}
            </code>
            <CopyButton text={getShareUrl()} field="link" />
          </div>
        </div>

        {/* Embed Button */}
        <div className="card-default p-6 space-y-4">
          <h2 className="font-heading font-semibold text-dark text-lg flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" />
            Embed Button
          </h2>
          <p className="text-sm text-text-secondary">
            Copy this HTML code to add a button to any website
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="text-xs text-text-secondary overflow-x-auto whitespace-pre-wrap">
              {getButtonCode()}
            </pre>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Preview:</p>
              <div className="mt-2">
                <a 
                  href={getShareUrl()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '12px 20px',
                    background: '#27B340',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: 600
                  }}
                >
                  {buttonText || 'Open My Direct Debits'}
                </a>
              </div>
            </div>
            <CopyButton text={getButtonCode()} field="button" />
          </div>
        </div>

        {/* Iframe Embed */}
        <div className="card-default p-6 space-y-4">
          <h2 className="font-heading font-semibold text-dark text-lg flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" />
            Iframe Embed (Optional)
          </h2>
          
          <div className="bg-gold-light border border-gold rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <p className="font-medium text-dark mb-1">Note about iframe embedding:</p>
              <p>
                Stripe checkout and some authentication flows may not work reliably within iframes due to browser security restrictions. 
                For the best user experience, we recommend using the <strong>direct link</strong> or <strong>embed button</strong> instead.
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="text-xs text-text-secondary overflow-x-auto whitespace-pre-wrap">
              {getIframeCode()}
            </pre>
          </div>
          
          <div className="flex justify-end">
            <CopyButton text={getIframeCode()} field="iframe" />
          </div>
        </div>

        {/* QR Code */}
        <div className="card-default p-6 space-y-4">
          <h2 className="font-heading font-semibold text-dark text-lg flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            QR Code
          </h2>
          <p className="text-sm text-text-secondary">
            Download or share this QR code for print materials
          </p>
          
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div 
              ref={qrRef}
              className="bg-white p-6 rounded-xl border border-border"
            >
              <QRCodeSVG 
                value={getShareUrl()}
                size={180}
                level="H"
                includeMargin={true}
              />
              <p className="text-center text-xs text-text-light mt-2">
                {sourceName ? `Source: ${sourceName}` : 'Direct link'}
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={downloadQRCode}
                className="btn-primary w-full gap-2"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4" />
                Download PNG
              </Button>
              <CopyButton text={getShareUrl()} field="qr-link" />
            </div>
          </div>
        </div>

        {/* Admin Link */}
        <div className="text-center pt-4">
          <Link 
            to="/admin" 
            className="text-primary text-sm hover:underline"
          >
            View Source Analytics →
          </Link>
        </div>
      </div>
    </PageLayout>
  );
}
